import { z } from "zod";
import { supabase, requireUserId } from "./sb-helpers";
import { setCachedTrip } from "./offline/trip-cache";
import { getActiveTripId } from "./active-trip";

/** Mongolia 2026 — the canonical default trip. Matches the DB constant in
 *  `get_default_trip_id()` and the auto-join trigger on auth.users. */
const DEFAULT_TRIP_ID = "092a4a07-2c0d-4e8c-92a3-77224c2a7fbd";

/** Get the current user's active trip + role.
 *
 *  Resolution order:
 *    1. Explicit tripId argument (if the user is a member).
 *    2. Persisted active trip in localStorage — ONLY if it exists, the user
 *       is a member, AND it is NOT a sample trip.
 *    3. Mongolia 2026 (the system default) if the user is a member.
 *    4. Most-recently-joined non-sample trip.
 *    5. Most-recently-joined trip (legacy fallback — includes sample trips).
 */
export async function getMyTrip(input?: { data?: { tripId?: string | null } } | unknown) {
  const userId = await requireUserId();
  const requested =
    (input as { data?: { tripId?: string | null } } | undefined)?.data?.tripId ?? null;
  const persisted = getActiveTripId(userId);

  // Fetch all memberships in one round-trip; resolve the preferred one in JS.
  const { data: memberships } = await supabase
    .from("trip_members")
    .select("trip_id, joined_at, submitted_at, trips(id, name, start_date, end_date, is_sample)")
    .eq("user_id", userId)
    .order("joined_at", { ascending: false });

  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);

  const isAdmin = (roles ?? []).some((r: any) => r.role === "admin");

  const list = (memberships ?? []) as Array<{
    trip_id: string;
    joined_at: string;
    submitted_at: string | null;
    trips:
      | { id: string; name: string; start_date: string; end_date: string; is_sample: boolean | null }
      | null;
  }>;
  const valid = list.filter((m) => !!m.trips);

  // 1. Explicit request wins if it's a real membership.
  let chosen = requested ? valid.find((m) => m.trip_id === requested) : undefined;

  // 2. Persisted active trip — honor any membership the user picked,
  //    including sample trips. The TripSwitcher only writes this value
  //    in response to an explicit user choice.
  if (!chosen && persisted) {
    const m = valid.find((m) => m.trip_id === persisted);
    if (m) chosen = m;
  }

  // 3. System default (Mongolia 2026) if the user is a member.
  if (!chosen) {
    chosen = valid.find((m) => m.trip_id === DEFAULT_TRIP_ID);
  }

  // 4. Most-recently-joined non-sample trip.
  if (!chosen) {
    chosen = valid.find((m) => !m.trips!.is_sample);
  }

  // 5. Legacy fallback — most-recently-joined, even if sample.
  if (!chosen) chosen = valid[0];

  if (!chosen?.trips) return { trip: null, isAdmin, mySubmittedAt: null as string | null };
  const t = chosen.trips;
  const result = {
    trip: { id: t.id, name: t.name, startDate: t.start_date, endDate: t.end_date },
    isAdmin,
    mySubmittedAt: (chosen.submitted_at as string | null) ?? null,
  };
  // Mirror to local cache so the bundled/offline shell can read trip identity
  // without a server round-trip. Fire-and-forget; never blocks the response.
  void setCachedTrip({
    id: t.id,
    name: t.name,
    startDate: t.start_date,
    endDate: t.end_date,
    isAdmin,
  }).catch(() => { /* ignore */ });
  return result;
}

/** All trips the current user is a member of, newest-joined first.
 *  Used by the header TripSwitcher to let users move between memberships. */
export async function listMyTrips(_input?: unknown) {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from("trip_members")
    .select(
      "trip_id, joined_at, submitted_at, trips(id, name, start_date, end_date, is_sample, sample_preset)",
    )
    .eq("user_id", userId)
    .order("joined_at", { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{
    trip_id: string;
    joined_at: string;
    submitted_at: string | null;
    trips: {
      id: string;
      name: string;
      start_date: string;
      end_date: string;
      is_sample: boolean | null;
      sample_preset: string | null;
    } | null;
  }>;
  return {
    trips: rows
      .filter((r) => !!r.trips)
      .map((r) => ({
        id: r.trips!.id,
        name: r.trips!.name,
        startDate: r.trips!.start_date,
        endDate: r.trips!.end_date,
        joinedAt: r.joined_at,
        submittedAt: r.submitted_at,
        isSample: !!r.trips!.is_sample,
        samplePreset: r.trips!.sample_preset,
      })),
  };
}

/** Mark the current user's trip section as submitted. */
export async function submitMyTrip(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  await requireUserId();
  const { data: ts, error } = await (supabase.rpc as any)("submit_my_trip", { _trip_id: data.tripId });
  if (error) throw new Error(error.message);
  return { submittedAt: (ts as string | null) ?? null };
}

/** Undo a previous submit for the current user. */
export async function unsubmitMyTrip(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  await requireUserId();
  const { error } = await (supabase.rpc as any)("unsubmit_my_trip", { _trip_id: data.tripId });
  if (error) throw new Error(error.message);
  return { ok: true };
}


const TripIdSchema = z.object({ tripId: z.string().uuid() });

const UpdateTripDatesSchema = z.object({
  tripId: z.string().uuid(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
});

/** Update a trip's start/end dates. RLS ("Admins manage trips") enforces admin-only. */
export async function updateTripDates(input: { data: z.infer<typeof UpdateTripDatesSchema> }) {
  const data = UpdateTripDatesSchema.parse(input.data);
  await requireUserId();
  if (data.startDate && data.endDate && data.endDate < data.startDate) {
    throw new Error("End date cannot be before start date");
  }
  const { error } = await supabase
    .from("trips")
    .update({ start_date: data.startDate, end_date: data.endDate })
    .eq("id", data.tripId);
  if (error) throw new Error(error.message);
  return { ok: true };
}

/** Admin-only: read the trip's shared invite code (RPC enforces is_trip_admin). */
export async function getTripInviteCode(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  await requireUserId();
  const { data: code, error } = await supabase.rpc("get_trip_invite_code", { _trip_id: data.tripId });
  if (error) throw new Error(error.message);
  return { code: (code as string | null) ?? null };
}

/** Admin-only: rotate the trip's shared invite code. */
export async function regenerateTripInviteCode(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  await requireUserId();
  const { data: code, error } = await supabase.rpc("regenerate_trip_invite_code", { _trip_id: data.tripId });
  if (error) throw new Error(error.message);
  return { code: code as string };
}

/** Trip members list (visible to fellow members and admins via RLS). */
export async function getTripMembers(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  await requireUserId();
  const { data: members, error } = await supabase
    .from("trip_members")
    .select("user_id, joined_at, color, submitted_at")
    .eq("trip_id", data.tripId);
  if (error) throw new Error(error.message);

  const ids = (members ?? []).map((m: any) => m.user_id);
  if (ids.length === 0) return { members: [] };
  const { data: profiles } = await (supabase.rpc as any)("get_profiles_basic", { _ids: ids });

  return {
    members: (members ?? []).map((m: any) => {
      const p = profiles?.find((x: any) => x.id === m.user_id);
      return {
        userId: m.user_id,
        joinedAt: m.joined_at,
        displayName: p?.display_name ?? "Traveler",
        avatarUrl: p?.avatar_url ?? null,
        color: (m.color as string | null) ?? null,
        submittedAt: (m.submitted_at as string | null) ?? null,
      };
    }),
  };
}

const UpdateMemberColorSchema = z.object({
  tripId: z.string().uuid(),
  userId: z.string().uuid(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
});

/** Admin-only (via RLS): assign a display color to a trip member. */
export async function updateTripMemberColor(input: { data: z.infer<typeof UpdateMemberColorSchema> }) {
  const data = UpdateMemberColorSchema.parse(input.data);
  await requireUserId();
  const { error } = await supabase
    .from("trip_members")
    .update({ color: data.color })
    .eq("trip_id", data.tripId)
    .eq("user_id", data.userId);
  if (error) throw new Error(error.message);
  return { ok: true };
}

/** Aggregated dashboard data: my stats + group stats. */
export async function getTripDashboard(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const tripId = data.tripId;

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const sodIso = startOfDay.toISOString();
  const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const [
    { data: media, error: mediaErr },
    { data: pings, error: pingsErr },
    { data: members, error: membersErr },
  ] = await Promise.all([
    supabase
      .from("media_assets")
      .select("user_id, media_type, upload_status, file_size_bytes, captured_at, uploaded_at, created_at")
      .eq("trip_id", tripId)
      .limit(10000),
    supabase
      .from("location_points")
      .select("user_id, captured_at, latitude, longitude")
      .eq("trip_id", tripId)
      .order("captured_at", { ascending: false })
      .limit(10000),
    supabase.from("trip_members").select("user_id").eq("trip_id", tripId),
  ]);

  if (mediaErr) throw new Error(mediaErr.message);
  if (pingsErr) throw new Error(pingsErr.message);
  if (membersErr) throw new Error(membersErr.message);

  const m = (media ?? []) as any[];
  const p = (pings ?? []) as any[];
  const mine = m.filter((x) => x.user_id === userId);
  const myPings = p.filter((x) => x.user_id === userId);
  const isUploaded = (x: any) => x.upload_status === "uploaded";
  const isPending = (x: any) => x.upload_status === "queued" || x.upload_status === "uploading";
  const isPhoto = (x: any) => x.media_type === "photo";
  const isVideo = (x: any) => x.media_type === "video";
  const isToday = (iso: string | null | undefined) => !!iso && iso >= sodIso;

  const mine_uploaded = mine.filter(isUploaded);
  const lastUpload =
    mine_uploaded
      .map((x) => x.uploaded_at ?? x.created_at)
      .filter(Boolean)
      .sort()
      .pop() ?? null;
  const lastPing = myPings[0]?.captured_at ?? null;

  // Today's biggest no-fix gap among my own pings. Used by the dashboard to
  // surface "you didn't record anything between A and B" instead of letting
  // a stale relative-time tile imply continuous recording.
  const myTodayPings = myPings
    .filter((x) => isToday(x.captured_at))
    .map((x) => ({
      capturedAt: x.captured_at as string,
      lat: Number(x.latitude),
      lng: Number(x.longitude),
    }))
    .filter((x) => Number.isFinite(x.lat) && Number.isFinite(x.lng))
    .sort((a, b) => (a.capturedAt < b.capturedAt ? -1 : 1));

  const haversineKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const R = 6371;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
  };

  const GAP_MIN_MS = 20 * 60 * 1000;
  let biggestGap: {
    startedAt: string;
    endedAt: string;
    durationMin: number;
    distanceKm: number;
    beforeCount: number;
    afterCount: number;
  } | null = null;
  for (let i = 0; i < myTodayPings.length - 1; i++) {
    const a = myTodayPings[i];
    const b = myTodayPings[i + 1];
    const dt = new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime();
    if (dt < GAP_MIN_MS) continue;
    if (biggestGap && dt <= biggestGap.durationMin * 60_000) continue;
    biggestGap = {
      startedAt: a.capturedAt,
      endedAt: b.capturedAt,
      durationMin: Math.round(dt / 60_000),
      distanceKm: haversineKm(a, b),
      beforeCount: i + 1,
      afterCount: myTodayPings.length - i - 1,
    };
  }

  const my = {
    photosUploaded: mine_uploaded.filter(isPhoto).length,
    videosUploaded: mine_uploaded.filter(isVideo).length,
    pendingUploads: mine.filter(isPending).length,
    lastUploadAt: lastUpload,
    lastLocationAt: lastPing,
    todayPhotos: mine_uploaded.filter((x) => isPhoto(x) && isToday(x.uploaded_at ?? x.created_at)).length,
    todayVideos: mine_uploaded.filter((x) => isVideo(x) && isToday(x.uploaded_at ?? x.created_at)).length,
    todayLocations: myTodayPings.length,
    todayBiggestGap: biggestGap,
  };

  const group_uploaded = m.filter(isUploaded);
  const activeTrackerSet = new Set(
    p.filter((x) => x.captured_at && x.captured_at >= last24h).map((x) => x.user_id),
  );
  const group = {
    totalMembers: (members ?? []).length,
    activeTrackers: activeTrackerSet.size,
    groupPhotos: group_uploaded.filter(isPhoto).length,
    groupVideos: group_uploaded.filter(isVideo).length,
    totalBytes: group_uploaded.reduce((s, x) => s + Number(x.file_size_bytes ?? 0), 0),
    groupPending: m.filter(isPending).length,
  };

  return { my, group };
}

/**
 * Read-only reconciliation count: how many `location_points` rows the server
 * confirms for the current user on a trip. Used by the diagnostics card on
 * /offline-control to compare against on-device native-queue / outbox / capture
 * counters so the user can see exactly where missing pings live.
 *
 * RLS already restricts `location_points` to trip members, so the head-only
 * `count: "exact"` here is safe — no row data leaves the server.
 */
export async function getPingReconciliation(input: { data: z.infer<typeof TripIdSchema> }) {
  const data = TripIdSchema.parse(input.data);
  const userId = await requireUserId();
  const { count, error } = await supabase
    .from("location_points")
    .select("id", { count: "exact", head: true })
    .eq("trip_id", data.tripId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
  return { serverCount: count ?? 0 };
}
